import React, { createContext, useContext, useEffect, useState } from 'react';

export const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [darkMode, setDarkMode] = useState(() => {
    // Check for saved theme preference or default to dark mode
    if (typeof window === 'undefined') return true; // Default to dark mode
    
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    
    // Default to dark mode instead of system preference
    return true;
  });

  // Toggle between dark and light mode
  const toggleTheme = () => {
    setDarkMode(prevMode => !prevMode);
  };

  // Update the data-theme attribute and class when darkMode changes
  useEffect(() => {
    const html = document.documentElement;
    const theme = darkMode ? 'dark' : 'light';
    
    // Set the theme in localStorage
    localStorage.setItem('theme', theme);
    
    // Set the data-theme attribute
    html.setAttribute('data-theme', theme);
    
    // Add or remove the 'dark' class for Tailwind CSS
    if (darkMode) {
      html.classList.add('dark');
    } else {
      html.classList.remove('dark');
    }

  }, [darkMode]);

  // Initial theme setup
  useEffect(() => {
    // Set initial theme - default to dark mode
    const savedTheme = localStorage.getItem('theme');
    const initialTheme = savedTheme || 'dark'; // Default to dark mode
    
    // Set the initial theme
    document.documentElement.setAttribute('data-theme', initialTheme);
    
    // Add or remove the 'dark' class for Tailwind CSS
    if (initialTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    setDarkMode(initialTheme === 'dark');
  }, []);

  return (
    <ThemeContext.Provider value={{ darkMode, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};
